import { useState, useEffect, useCallback } from 'react'

interface ListOption {
    id: number
    value: string
    label: string
    color?: string
    translations?: any[]
    settings?: any
    default?: boolean
}

interface SelectedOption {
    id: number
    value: string
    label: string
    color?: string
    translations?: any[]
}

interface UseListFieldDataProps {
    item: any
    value: any
    mode: 'advanced-search' | 'edit' | 'search' | 'show/edit' | 'show'
    dossier?: any
    kedo: any
    shoudListenForConditions?: boolean
    recheckConditions?: () => void
    changeValue?: (id: string, value: any) => void
}

interface UseListFieldDataReturn {
    options: ListOption[]
    selected: SelectedOption[]
    search: string | null
    page: number
    total: number
    optionsLoading: boolean
    fastSwitchLoading: boolean
    isAdmin: boolean
    mode: string
    selectedOptions: ListOption[]
    searchOptions: ListOption[]
    setSearch: (search: string | null) => void
    setPage: (page: number) => void
    setSelected: (selected: SelectedOption[]) => void
    addSelectedItem: (item: ListOption) => void
    removeSelectedItem: (item: SelectedOption) => void
    setSingleSelectByOptionId: (itemSelect: any) => void
    getSelectableOptions: (
        options: ListOption[],
        selects: SelectedOption[],
        search: string | null
    ) => ListOption[]
    getCurrentTranslationItem: (listOption: ListOption, locale: any) => any
    getCurrentTranslationItemText: (
        listOption: ListOption,
        locale: any
    ) => string
    isSingleSelect: () => boolean
    listOptionsHasVisibilitySettings: (itemId: number) => boolean
    onFastSwitchChange: (item: any) => void
    refetch: () => void
}

const isAdvancedSearchMode = (
    mode: 'advanced-search' | 'edit' | 'search' | 'show/edit' | 'show'
): boolean => {
    return mode === 'advanced-search'
}

export const useListFieldData = (
    props: UseListFieldDataProps
): UseListFieldDataReturn => {
    const {
        item,
        value,
        mode,
        dossier,
        kedo,
        shoudListenForConditions,
        recheckConditions,
        changeValue,
    } = props

    const [options, setOptions] = useState<ListOption[]>(
        () => item.def_dossier_def_field.def_field.def_field_lists
    )
    const [selected, setSelected] = useState<SelectedOption[]>(() => {
        const selected: SelectedOption[] = []

        if (value && value.length > 0 && value.map) {
            value.map((item: any) => selected.push(item))
        } else if (value && value.length > 0) {
            selected.push(value)
        } else if (
            item.view !== 'search' &&
            !isAdvancedSearchMode(mode) &&
            (!dossier || mode === 'edit' || mode === 'show/edit') &&
            (item.def_dossier_def_field.def_field.def_field_lists.find(
                (item: any) => item.default === true
            ) ||
                (item.settings &&
                    item.settings.default_list_option &&
                    item.def_dossier_def_field.def_field.def_field_lists.find(
                        (item: any) =>
                            item.id ===
                            parseInt(item.settings.default_list_option)
                    )))
        ) {
            const defaultItem =
                item.def_dossier_def_field.def_field.def_field_lists.find(
                    (item: any) => item.default === true
                )
            const defaultOption =
                item.settings &&
                item.settings.default_list_option &&
                item.def_dossier_def_field.def_field.def_field_lists.find(
                    (item: any) =>
                        item.id === parseInt(item.settings.default_list_option)
                )

            if (defaultOption) {
                selected.push(defaultOption)
            } else {
                selected.push(defaultItem)
            }
        }
        return selected
    })

    const [search, setSearch] = useState<string | null>(null)
    const [page, setPage] = useState<number>(0)
    const [optionsLoading, setOptionsLoading] = useState<boolean>(false)
    const [fastSwitchLoading, setFastSwitchLoading] = useState<boolean>(false)
    const [selectedOptions, setSelectedOptions] = useState<ListOption[]>([])
    const [searchOptions, setSearchOptions] = useState<ListOption[]>([])
    const [total, setTotal] = useState<number>(
        () => item.def_dossier_def_field.def_field.def_field_lists.length
    )
    const [isAdmin, setIsAdmin] = useState<boolean>(() =>
        kedo.isAdminOrEnvironmentAdmin()
    )

    const [componentMode, setComponentMode] = useState<string>('show')

    useEffect(() => {
        if (
            (mode === 'edit' || mode === 'show/edit') &&
            componentMode === 'show'
        ) {
            const selectedDefaults = (): SelectedOption[] => {
                const selected: SelectedOption[] = []

                if (value && value.length > 0 && value.map) {
                    value.map((item: any) => selected.push(item))
                } else if (value && value.length > 0) {
                    selected.push(value)
                } else if (
                    item.view !== 'search' &&
                    !isAdvancedSearchMode(mode) &&
                    (!dossier || mode === 'edit' || mode === 'show/edit') &&
                    (item.def_dossier_def_field.def_field.def_field_lists.find(
                        (item: any) => item.default === true
                    ) ||
                        (item.settings &&
                            item.settings.default_list_option &&
                            item.def_dossier_def_field.def_field.def_field_lists.find(
                                (item: any) =>
                                    item.id ===
                                    parseInt(item.settings.default_list_option)
                            )))
                ) {
                    const defaultItem =
                        item.def_dossier_def_field.def_field.def_field_lists.find(
                            (item: any) => item.default === true
                        )
                    const defaultOption =
                        item.settings &&
                        item.settings.default_list_option &&
                        item.def_dossier_def_field.def_field.def_field_lists.find(
                            (item: any) =>
                                item.id ===
                                parseInt(item.settings.default_list_option)
                        )

                    if (defaultOption) {
                        selected.push(defaultOption)
                    } else {
                        selected.push(defaultItem)
                    }
                }
                return selected
            }

            setComponentMode('edit')
            setSelected(selectedDefaults())
        }

        if (mode === 'show' && componentMode === 'edit') {
            setComponentMode('show')
        }
    }, [mode, componentMode])

    // Data fetching and processing methods
    const getCurrentTranslationItem = useCallback(
        (listOption: ListOption, locale: any) => {
            if (listOption.translations && listOption.translations.length > 0) {
                const trans = listOption.translations.find(
                    (option: any) => option.culture === locale.code
                )
                if (trans && trans.text) {
                    return trans
                }
            }
            return false
        },
        []
    )

    const getCurrentTranslationItemText = useCallback(
        (listOption: ListOption, locale: any): string => {
            const found = getCurrentTranslationItem(listOption, locale)
            if (found) {
                return found.text
            }

            if (listOption.translations && listOption.translations.length > 0) {
                return listOption.translations[0].text
            }

            return listOption.id.toString()
        },
        [getCurrentTranslationItem]
    )

    const isSingleSelect = useCallback((): boolean => {
        const defField = item.def_dossier_def_field.def_field
        if (defField?.settings?.multiple) {
            return false
        }
        return true
    }, [item])

    const listOptionsHasVisibilitySettings = useCallback(
        (itemId: number): boolean => {
            const searchVisibility =
                item.def_dossier_def_field.def_field.def_field_lists
            const foundItem = searchVisibility.find(
                (item: any) => item.id === itemId
            )

            if (foundItem) {
                const visibility = foundItem.settings?.roles
                if (visibility && visibility.length > 0) {
                    return true
                }
            }
            return false
        },
        [item]
    )

    const addSelectedItem = useCallback(
        (item: ListOption) => {
            let select = selected

            if (isSingleSelect() === true && select.length > 0) {
                return
            }

            select = select.filter((opt) => opt.id !== item.id)
            select.push(item)

            if (changeValue) {
                changeValue(props.item.id, select)
            }

            setSelected(select)
            if (recheckConditions) {
                recheckConditions()
            }
        },
        [
            selected,
            isSingleSelect,
            changeValue,
            recheckConditions,
            props.item.id,
        ]
    )

    const removeSelectedItem = useCallback(
        (item: SelectedOption) => {
            setSelected((prevSelected) => {
                const updatedSelected = prevSelected.filter(
                    (opt) => opt.id !== item.id
                )
                recheckConditions?.()
                return updatedSelected
            })
        },
        [recheckConditions]
    )

    const setSingleSelectByOptionId = useCallback(
        (itemSelect: any) => {
            let curItem = null
            if (Array.isArray(itemSelect)) {
                curItem = itemSelect.length > 1 ? itemSelect[1] : itemSelect[0]
            } else {
                curItem = itemSelect
            }

            if (!isSingleSelect()) {
                return
            }

            if (!curItem) {
                if (changeValue) {
                    changeValue(item.id, [])
                }

                setSelected([])
                if (shoudListenForConditions) {
                    recheckConditions?.()
                }
                return
            }

            const selectedOption = options.find(
                (opt) => opt.id === parseInt(curItem.id)
            )
            const selectedItem: SelectedOption = {
                color: curItem.color,
                id: curItem.id,
                value: curItem.value,
                label: curItem.label,
                translations: curItem.translations,
            }

            setSelected([])
            if (changeValue) {
                changeValue(item.id, [selectedOption])
            }

            setSelected(!selectedOption ? [] : [selectedItem])
            if (shoudListenForConditions) {
                recheckConditions?.()
            }
        },
        [
            isSingleSelect,
            options,
            changeValue,
            shoudListenForConditions,
            recheckConditions,
        ]
    )

    const getSelectableOptions = useCallback(
        (
            options: ListOption[],
            selects: SelectedOption[],
            search: string | null
        ): ListOption[] => {
            const selectedOptions = options.filter((opt) => {
                const found = selects.find((select) => opt.id === select.id)
                return !found || found === undefined
            })

            if (search) {
                const searchUpper = search.toUpperCase()
                const locale = kedo.env().getSelectedLocale()
                return selectedOptions.filter((opt) => {
                    const trans = getCurrentTranslationItem(opt, locale)

                    if (!trans) {
                        return false
                    }

                    return trans.text.toUpperCase().indexOf(searchUpper) > -1
                })
            }

            return selectedOptions
        },
        [getCurrentTranslationItem, kedo]
    )

    const onFastSwitchChange = useCallback(
        (item: any) => {
            const singleSelect = isSingleSelect()
            let curSelected = selected
            const api = kedo.api()
            const dossierId = dossier.id

            setFastSwitchLoading(true)

            if (singleSelect) {
                const values: number[] = []
                if (item && item.id) {
                    values.push(item.id)
                }

                api.post(
                    api.getContentListEndpoint(dossierId, props.item.id, 'set'),
                    values
                ).then((response: any) => {
                    setComponentMode('show')
                    setSelected(item && item.id ? [item] : [])
                    setFastSwitchLoading(false)
                })
            } else {
                const changedItems: any[] = []

                if (Array.isArray(item)) {
                    item.forEach((newItem: any) => {
                        const existing = curSelected.find(
                            (selItem) => selItem.id === newItem.id
                        )
                        if (!existing) {
                            changedItems.push({ mode: 'new', item: newItem })
                        }
                    })
                }

                curSelected.forEach((curItem) => {
                    const isFound =
                        item &&
                        item.find((newItem: any) => curItem.id === newItem.id)
                    if (!isFound) {
                        changedItems.push({ mode: 'remove', item: curItem })
                    }
                })

                changedItems.forEach((changedItem, index) => {
                    if (changedItem.mode === 'new') {
                        curSelected.push(changedItem.item)
                        api.post(
                            api.getContentListEndpoint(
                                dossierId,
                                item.id,
                                'add',
                                changedItem.item.id
                            )
                        ).then((response: any) => {
                            if (changedItems.length === index + 1) {
                                setComponentMode('show')
                                setSelected(curSelected)
                                setFastSwitchLoading(false)
                            }
                        })
                    } else {
                        curSelected = curSelected.filter(
                            (curItem) => curItem.id !== changedItem.item.id
                        )
                        api.delete(
                            api.getContentListEndpoint(
                                dossierId,
                                item.id,
                                'remove',
                                changedItem.item.id
                            )
                        ).then((response: any) => {
                            if (changedItems.length === index + 1) {
                                setComponentMode('show')
                                setSelected(
                                    curSelected.filter(
                                        (curItem) =>
                                            curItem.id !== changedItem.item.id
                                    )
                                )
                                setFastSwitchLoading(false)
                            }
                        })
                    }
                })
            }
        },
        [isSingleSelect, selected, kedo, dossier, setComponentMode]
    )

    const refetch = useCallback(() => {
        // Reset to initial state
        setOptions(item.def_dossier_def_field.def_field.def_field_lists)
        setTotal(item.def_dossier_def_field.def_field.def_field_lists.length)

        // Recalculate selected items
        const selectedDefaults = (): SelectedOption[] => {
            const selected: SelectedOption[] = []

            if (value && value.length > 0 && value.map) {
                value.map((item: any) => selected.push(item))
            } else if (value && value.length > 0) {
                selected.push(value)
            } else if (
                item.view !== 'search' &&
                !isAdvancedSearchMode(mode) &&
                (!dossier || mode === 'edit' || mode === 'show/edit') &&
                (item.def_dossier_def_field.def_field.def_field_lists.find(
                    (item: any) => item.default === true
                ) ||
                    (item.settings &&
                        item.settings.default_list_option &&
                        item.def_dossier_def_field.def_field.def_field_lists.find(
                            (item: any) =>
                                item.id ===
                                parseInt(item.settings.default_list_option)
                        )))
            ) {
                const defaultItem =
                    item.def_dossier_def_field.def_field.def_field_lists.find(
                        (item: any) => item.default === true
                    )
                const defaultOption =
                    item.settings &&
                    item.settings.default_list_option &&
                    item.def_dossier_def_field.def_field.def_field_lists.find(
                        (item: any) =>
                            item.id ===
                            parseInt(item.settings.default_list_option)
                    )

                if (defaultOption) {
                    selected.push(defaultOption)
                } else {
                    selected.push(defaultItem)
                }
            }
            return selected
        }

        setSelected(selectedDefaults())
    }, [item, value, mode, dossier])

    return {
        options,
        selected,
        search,
        page,
        total,
        optionsLoading,
        fastSwitchLoading,
        isAdmin,
        mode: componentMode,
        selectedOptions,
        searchOptions,
        setSearch,
        setPage,
        setSelected,
        addSelectedItem,
        removeSelectedItem,
        setSingleSelectByOptionId,
        getSelectableOptions,
        getCurrentTranslationItem,
        getCurrentTranslationItemText,
        isSingleSelect,
        listOptionsHasVisibilitySettings,
        onFastSwitchChange,
        refetch,
    }
}
